import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  const lastUpdated = "February 11, 2025";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <Link to="/login">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
          <p className="text-gray-600 mt-2">Last updated: {lastUpdated}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Anglin Sports Consultancy ("we," "our," or "us") operates the Athlete Attendance App Athlete 
              Attendance Management system ("Service"). This Privacy Policy explains how we collect, use, 
              disclose, and safeguard your information when you use our Service. Please read this policy 
              carefully. By using the Service, you consent to the practices described in this Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.1 Personal Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We collect personal information that you voluntarily provide when using the Service, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Athlete Information:</strong> Name, date of birth, email address, phone number, emergency contact details</li>
              <li><strong>Administrator Information:</strong> Username and authentication credentials</li>
              <li><strong>Program Information:</strong> Program names, schedules, and associated athlete assignments</li>
              <li><strong>Attendance Records:</strong> Date, time, and status of athlete attendance</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.2 Automatically Collected Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you access the Service, we may automatically collect certain information, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Browser type and version</li>
              <li>Device information and operating system</li>
              <li>IP address and general location data</li>
              <li>Access times and dates</li>
              <li>Pages viewed and features used within the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>To provide and maintain the Service</li>
              <li>To manage athlete rosters and track attendance</li>
              <li>To generate reports and analytics for coaching staff</li>
              <li>To authenticate users and secure access to the Service</li>
              <li>To communicate with you about Service updates or issues</li>
              <li>To improve and optimize the Service</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Data Storage and Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement appropriate technical and organizational measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Encrypted data transmission using HTTPS/TLS</li>
              <li>Secure password hashing for authentication credentials</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls limiting data access to authorized personnel</li>
              <li>Secure cloud infrastructure with regular backups</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              While we strive to protect your information, no method of electronic storage or transmission 
              is 100% secure. We cannot guarantee absolute security of your data.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share 
              your information only in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Service Providers:</strong> With trusted third-party service providers who assist 
              in operating the Service, subject to confidentiality agreements</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
              <li><strong>Protection of Rights:</strong> To protect our rights, privacy, safety, or property, 
              or that of our users or others</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We retain personal information for as long as necessary to fulfill the purposes outlined 
              in this Privacy Policy, unless a longer retention period is required or permitted by law. 
              When athlete data is no longer needed for its intended purpose, we will securely delete or 
              anonymize the information. Attendance records may be retained for historical reporting 
              purposes as required by the organization.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Depending on your jurisdiction, you may have certain rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal retention requirements</li>
              <li><strong>Restriction:</strong> Request restriction of processing of your personal information</li>
              <li><strong>Portability:</strong> Request transfer of your data in a structured, machine-readable format</li>
              <li><strong>Objection:</strong> Object to certain types of processing of your personal information</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise any of these rights, please contact us using the information provided below.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              The Service may contain information about minor athletes as part of sports program management. 
              We collect and process such information only as necessary for legitimate attendance tracking 
              purposes and with appropriate parental or guardian consent. We do not knowingly collect 
              personal information directly from children under 13 without parental consent. If you believe 
              we have collected information from a child without proper consent, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed">
              The Service uses cookies and similar tracking technologies to maintain user sessions and 
              improve functionality. These are essential cookies required for the Service to operate 
              properly. By using the Service, you consent to the use of these essential cookies. 
              You can configure your browser to refuse cookies, but this may prevent you from using 
              certain features of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              Your information may be transferred to and processed in countries other than your country 
              of residence. These countries may have different data protection laws. By using the Service, 
              you consent to the transfer of your information to such countries. We will take appropriate 
              measures to ensure your information remains protected in accordance with this Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or 
              legal requirements. We will notify you of any material changes by posting the updated policy 
              on the Service with a new "Last Updated" date. Your continued use of the Service after such 
              changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our 
              data practices, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700">
                <strong>Anglin Sports Consultancy</strong><br />
                Email:{' '}
                <a href="mailto:info@attendancetracker.ws" className="text-blue-600 hover:underline">
                  info@attendancetracker.ws
                </a>
              </p>
            </div>
          </section>
        </div>

        <div className="mt-8 text-center text-sm text-gray-600">
          © 2025 Anglin Sports Consultancy. Built for coaches.
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
